/* eslint-disable no-sparse-arrays */
import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { BANNER_API_BASE_URL, getApiHeaders } from '../config/api';

type BannerItem = {
  navUrl: string;
  navType: 'WEB' | 'SCREEN';
  priority: number;
  expiresAt?: string; // ISO date string
  imageFile?: File;
  imagePreview?: string;
  title?: string;
  description?: string;
};

type Section = {
  sectionNumber: number;
  maxDisplay: number;
  banners: BannerItem[];
};

const SCREENS = ['HOME', 
  'EXPLORE', 
  'WALLET',
  'CHECKOUT', 
  'PROFILE', 
  'CART', 
  'SHOP', 
  'TASK',
  'LEADERBOARD PAGE',
  'EXCLUSIVE PAGE',
  'CONTACT',
  'PROFILE',
  'SEE ALL PRIZES'
  , ];

const CreateBanner: React.FC = () => {
  const navigate = useNavigate();
  const [screen, setScreen] = useState('');
  const [sections, setSections] = useState<Section[]>([
    { sectionNumber: 1, maxDisplay: 5, banners: [{ navUrl: '', navType: 'WEB', priority: 1 }] }
  ]);
  const [uploading, setUploading] = useState(false);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('wallet_assets_user_id');
    
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    
    if (!userId) {
      toast.error('User ID not found. Please log in again.');
      return null;
    }
    
    const baseHeaders = getApiHeaders();
    const headers: Record<string, string> = {
      'x-api-key': baseHeaders['x-api-key'],
      'userid': userId,
      'accesstoken': token,
    };
    return headers;
  }, []);

  // Section operations
  const addSection = () => {
    const newSectionNumber = sections.length + 1;
    setSections([
      ...sections,
      { sectionNumber: newSectionNumber, maxDisplay: 5, banners: [{ navUrl: '', navType: 'WEB', priority: 1 }] }
    ]);
  };

  const removeSection = (sectionIndex: number) => {
    if (sections.length === 1) {
      toast.error('At least one section is required');
      return;
    }
    setSections(sections.filter((_, i) => i !== sectionIndex));
  };

  const updateSectionMaxDisplay = (sectionIndex: number, maxDisplay: number) => {
    const updated = [...sections];
    updated[sectionIndex].maxDisplay = maxDisplay;
    setSections(updated);
  };

  // Banner operations
  const addBanner = (sectionIndex: number) => {
    const updated = [...sections];
    updated[sectionIndex].banners.push({
      navUrl: '',
      navType: 'WEB',
      priority: updated[sectionIndex].banners.length + 1
    });
    setSections(updated);
  };

  const removeBanner = (sectionIndex: number, bannerIndex: number) => {
    const updated = [...sections];
    if (updated[sectionIndex].banners.length === 1) {
      toast.error('At least one banner is required per section');
      return;
    }
    updated[sectionIndex].banners = updated[sectionIndex].banners.filter((_, i) => i !== bannerIndex);
    setSections(updated);
  };

  const updateBanner = (sectionIndex: number, bannerIndex: number, field: keyof BannerItem, value: any) => {
    const updated = [...sections];
    updated[sectionIndex].banners[bannerIndex] = {
      ...updated[sectionIndex].banners[bannerIndex],
      [field]: value
    };
    setSections(updated);
  };

  const handleImageChange = (sectionIndex: number, bannerIndex: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const updated = [...sections];
      updated[sectionIndex].banners[bannerIndex].imageFile = file;
      updated[sectionIndex].banners[bannerIndex].imagePreview = URL.createObjectURL(file);
      setSections(updated);
    }
  };

  const handleSubmit = async () => {
    if (!screen) {
      toast.error('Please select a screen');
      return;
    }

    const headers = headerWithAuth();
    if (!headers) return;

    setUploading(true);

    try {
      // Step 1: Create screen with sections
      const screenPayload = {
        screen,
        sections: sections.map(s => ({
          sectionNumber: s.sectionNumber,
          maxDisplay: s.maxDisplay
        }))
      };

      const screenRes = await fetch(`${BANNER_API_BASE_URL}/banners/screen`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(screenPayload)
      });

      const screenJson = await screenRes.json();
      if (!screenRes.ok) {
        throw new Error(screenJson.message || 'Failed to create screen');
      }

      const createdSections = screenJson.data.sections;

      // Step 2: Add banners to each section
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const createdSection = createdSections[i];

        for (const banner of section.banners) {
          if (!banner.imageFile) {
            toast.error(`Missing image for a banner in Section ${section.sectionNumber}`);
            continue;
          }

          const formData = new FormData();
          formData.append('navUrl', banner.navUrl);
          formData.append('navType', banner.navType);
          formData.append('priority', banner.priority.toString());
          if (banner.expiresAt) {
            formData.append('expiresAt', banner.expiresAt);
          }
          if (banner.title) {
            formData.append('title', banner.title);
          }
          if (banner.description) {
            formData.append('description', banner.description);
          }
          formData.append('image', banner.imageFile);

          const bannerRes = await fetch(
            `${BANNER_API_BASE_URL}/banners/screen/${screen}/section/${createdSection._id}/banner`,
            {
              method: 'POST',
              headers,
              body: formData
            }
          );

          const bannerJson = await bannerRes.json();
          if (!bannerRes.ok) {
            throw new Error(bannerJson.message || 'Failed to add banner');
          }
        }
      }

      toast.success('Banners created successfully!');
      
      // Reset form
      setScreen('');
      setSections([{ sectionNumber: 1, maxDisplay: 5, banners: [{ navUrl: '', navType: 'WEB', priority: 1 }] }]);
      
      // Navigate to banners list
      setTimeout(() => navigate('/banners'), 1000);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to create banners');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900'>
      <Navbar />
      
      <div className='container mx-auto px-4 py-8'>
        {/* Header */}
        <div className='mb-8'>
          <button
            onClick={() => navigate('/banners')}
            className='flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-4'
          >
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
            </svg>
            Back to Banners
          </button>
          <h1 className='text-4xl font-bold text-white mb-2'>Create Banners</h1>
          <p className='text-gray-400'>Add promotional banners with multiple sections</p>
        </div>

        {/* Main Form */}
        <div className='bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl'>
          {/* Screen Selection */}
          <div className='mb-8'>
            <label className='block text-sm font-medium text-gray-300 mb-2'>
              Screen *
            </label>
            <select
              value={screen}
              onChange={(e) => setScreen(e.target.value)}
              className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
            >
              <option value=''>Select a screen</option>
              {SCREENS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Sections */}
          <div className='space-y-6'>
            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className='bg-white/5 border border-white/10 rounded-xl p-6'>
                {/* Section Header */}
                <div className='flex items-center justify-between mb-4'>
                  <h3 className='text-xl font-bold text-white'>Section {section.sectionNumber}</h3>
                  <div className='flex items-center gap-4'>
                    <div className='flex items-center gap-2'>
                      <label className='text-sm text-gray-300'>Max Display:</label>
                      <input
                        type='number'
                        value={section.maxDisplay}
                        onChange={(e) => updateSectionMaxDisplay(sectionIndex, parseInt(e.target.value))}
                        min='1'
                        className='w-20 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500'
                      />
                    </div>
                    {sections.length > 1 && (
                      <button
                        onClick={() => removeSection(sectionIndex)}
                        className='px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all'
                      >
                        Remove Section
                      </button>
                    )}
                  </div>
                </div>

                {/* Banners in Section */}
                <div className='space-y-4'>
                  {section.banners.map((banner, bannerIndex) => (
                    <div key={bannerIndex} className='bg-white/5 border border-white/10 rounded-lg p-4'>
                      <div className='flex items-start justify-between mb-4'>
                        <h4 className='text-lg font-semibold text-white'>Banner {bannerIndex + 1}</h4>
                        {section.banners.length > 1 && (
                          <button
                            onClick={() => removeBanner(sectionIndex, bannerIndex)}
                            className='text-red-400 hover:text-red-300'
                          >
                            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                            </svg>
                          </button>
                        )}
                      </div>

                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        {/* Navigation Type */}
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>Navigation Type *</label>
                          <select
                            value={banner.navType}
                            onChange={(e) => updateBanner(sectionIndex, bannerIndex, 'navType', e.target.value)}
                            className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500'
                          >
                            <option value='WEB'>Web URL</option>
                            <option value='SCREEN'>App Screen</option>
                          </select>
                        </div>

                        {/* Navigation URL or Screen Selector */}
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>
                            {banner.navType === 'WEB' ? 'Navigation URL *' : 'Page to Navigate To *'}
                          </label>
                         
                            <input
                              type='text'
                              value={banner.navUrl}
                              onChange={(e) => updateBanner(sectionIndex, bannerIndex, 'navUrl', e.target.value)}
                              placeholder='https://example.com'
                              className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500'
                            />
                          
                          
                        </div>

                        {/* Priority */}
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>Priority</label>
                          <input
                            type='number'
                            value={banner.priority}
                            onChange={(e) => updateBanner(sectionIndex, bannerIndex, 'priority', parseInt(e.target.value))}
                            min='1'
                            className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500'
                          />
                        </div>

                        {/* Expiration Date */}
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>Expiration (Optional)</label>
                          <input
                            type='datetime-local'
                            value={banner.expiresAt || ''}
                            onChange={(e) => updateBanner(sectionIndex, bannerIndex, 'expiresAt', e.target.value)}
                            className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500'
                          />
                        </div>

                        {/* Image Upload */}
                        <div className='md:col-span-2'>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>Banner Image *</label>
                          <input
                            type='file'
                            accept='image/*'
                            onChange={(e) => handleImageChange(sectionIndex, bannerIndex, e)}
                            className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-purple-500 file:text-white hover:file:bg-purple-600 focus:outline-none'
                          />
                          {banner.imagePreview && (
                            <img
                              src={banner.imagePreview}
                              alt='Preview'
                              className='mt-4 max-h-48 rounded-lg border border-white/20'
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add Banner Button */}
                  <button
                    onClick={() => addBanner(sectionIndex)}
                    className='w-full py-3 border-2 border-dashed border-white/20 rounded-lg text-gray-300 hover:border-purple-500 hover:text-purple-400 transition-all'
                  >
                    + Add Another Banner to Section {section.sectionNumber}
                  </button>
                </div>
              </div>
            ))}

            {/* Add Section Button */}
            <button
              onClick={addSection}
              className='w-full py-4 border-2 border-dashed border-white/30 rounded-xl text-white hover:border-purple-500 hover:bg-purple-500/10 transition-all font-semibold'
            >
              + Add Section {sections.length + 1}
            </button>
          </div>

          {/* Submit Button */}
          <div className='mt-8 flex gap-4'>
            <button
              onClick={() => navigate('/banners')}
              className='flex-1 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all'
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={uploading}
              className='flex-1 py-4 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {uploading ? 'Creating...' : 'Create Banners'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBanner;
